import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "wouter";
import {
  Users, BarChart3, Flag, Shield, Activity,
  Search, ChevronLeft, ChevronRight, ToggleLeft, ToggleRight,
  CheckCircle, XCircle, Loader2, LogOut,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type Tab = "overview" | "users" | "flags" | "audit";

const STATUS_COLORS: Record<string, string> = {
  rd:   "bg-orange-100 text-orange-700 border-orange-200",
  beta: "bg-purple-100 text-purple-700 border-purple-200",
  live: "bg-green-100 text-green-700 border-green-200",
};

const STATUS_LABELS: Record<string, string> = {
  rd:   "R&D",
  beta: "Beta",
  live: "Live",
};

async function adminFetch(path: string, options?: RequestInit) {
  const token = localStorage.getItem("commonqr_token");
  const res = await fetch(`/api/v1/admin${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`,
      ...options?.headers,
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export default function Admin() {
  const [, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  // Overview
  const [overview, setOverview] = useState<any>(null);

  // Users
  const [users, setUsers] = useState<any[]>([]);
  const [userSearch, setUserSearch] = useState("");
  const [userPage, setUserPage] = useState(1);
  const [userTotal, setUserTotal] = useState(0);
  const [userPages, setUserPages] = useState(1);

  // Feature flags
  const [flags, setFlags] = useState<any[]>([]);
  const [flagUpdating, setFlagUpdating] = useState<string | null>(null);

  // Audit log
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [auditPage, setAuditPage] = useState(1);

  // ── Auth check ──────────────────────────────────────────────────────────────
  useEffect(() => {
    adminFetch("/overview")
      .then((data) => {
        setOverview(data);
        setAuthorized(true);
        setLoading(false);
      })
      .catch(() => {
        setLocation("/dashboard");
      });
  }, []);

  // ── Load users ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== "users") return;
    adminFetch(`/users?search=${encodeURIComponent(userSearch)}&page=${userPage}`)
      .then((data) => {
        setUsers(data.users);
        setUserTotal(data.total);
        setUserPages(data.pages);
      })
      .catch(() => {});
  }, [activeTab, userSearch, userPage]);

  // ── Load feature flags ──────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== "flags") return;
    adminFetch("/feature-flags").then(setFlags).catch(() => {});
  }, [activeTab]);

  // ── Load audit log ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== "audit") return;
    adminFetch(`/audit-log?page=${auditPage}`)
      .then((data) => setAuditLogs(data.logs))
      .catch(() => {});
  }, [activeTab, auditPage]);

  const toggleFlag = async (key: string, currentStatus: string) => {
    const next = currentStatus === "rd" ? "beta" : currentStatus === "beta" ? "live" : "rd";
    setFlagUpdating(key);
    try {
      await adminFetch(`/feature-flags/${key}`, {
        method: "POST",
        body: JSON.stringify({ status: next }),
      });
      setFlags((prev) => prev.map((f) => f.featureKey === key ? { ...f, status: next } : f));
    } catch {}
    setFlagUpdating(null);
  };

  const toggleUserSuspend = async (userId: number) => {
    try {
      const res = await adminFetch(`/users/${userId}/suspend`, { method: "POST" });
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, isActive: res.isActive } : u));
    } catch {}
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-indigo-400" />
      </div>
    );
  }

  if (!authorized) return null;

  const TABS = [
    { id: "overview", label: "Overview",      icon: BarChart3 },
    { id: "users",    label: "Users",          icon: Users },
    { id: "flags",    label: "Feature Flags",  icon: Flag },
    { id: "audit",    label: "Audit Log",      icon: Activity },
  ] as const;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Super Admin — CommonQR</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* R&D Mode indicator bar */}
      <div className="w-full bg-orange-500 text-white text-xs font-bold text-center py-1.5 tracking-widest uppercase">
        ⚠ Super Admin Mode — Internal Use Only
      </div>

      {/* Header */}
      <div className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-indigo-400" />
          <span className="font-bold text-lg">CommonQR Admin</span>
        </div>
        <div className="flex items-center gap-4">
           <span className="text-xs text-muted-foreground">Super Admin Dashboard</span>
          <button
            onClick={() => setLocation("/dashboard")}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <LogOut className="w-4 h-4" /> Exit Admin
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-52 shrink-0 border-r border-border min-h-screen p-4">
          <nav className="space-y-1">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    activeTab === tab.id
                       ? "bg-indigo-600 text-white"
                       : "text-muted-foreground hover:bg-card hover:text-foreground"
                  }`}
                >
                  <Icon className="w-4 h-4" /> {tab.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 p-6">

          {/* ── Overview ── */}
          {activeTab === "overview" && overview && (
            <div>
              <h1 className="text-2xl font-bold mb-6">Platform Overview</h1>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {[
                  { label: "Total Users",    value: overview.users?.totalUsers ?? 0,         sub: `${overview.users?.todayUsers ?? 0} today` },
                  { label: "Verified Users", value: overview.users?.verifiedUsers ?? 0,       sub: "email verified" },
                  { label: "Total Profiles", value: overview.profiles?.totalProfiles ?? 0,    sub: "QR profiles" },
                  { label: "Total Scans",    value: overview.scans?.totalScans ?? 0,          sub: `${overview.scans?.todayScans ?? 0} today` },
                ].map((stat, i) => (
                  <div key={i} className="bg-card rounded-2xl p-5 border border-border">
                    <p className="text-muted-foreground text-xs font-medium uppercase tracking-wider mb-2">{stat.label}</p>
                    <p className="text-3xl font-bold text-foreground">{stat.value.toLocaleString()}</p>
                    <p className="text-muted-foreground text-xs mt-1">{stat.sub}</p>
                  </div>
                ))}
              </div>
              <div className="bg-card rounded-2xl p-5 border border-border">
                <p className="text-muted-foreground text-xs font-medium uppercase tracking-wider mb-2">Active Subscriptions</p>
                <p className="text-3xl font-bold text-green-400">{overview.subscriptions?.activeSubscriptions ?? 0}</p>
              </div>
            </div>
          )}

          {/* ── Users ── */}
          {activeTab === "users" && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold">Users ({userTotal})</h1>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={userSearch}
                    onChange={(e) => { setUserSearch(e.target.value); setUserPage(1); }}
                    placeholder="Search by email, username, name..."
                    className="pl-9 w-72 bg-card border-border text-foreground placeholder:text-muted-foreground"
                  />
                </div>
              </div>

              <div className="bg-card rounded-2xl border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left px-4 py-3 text-muted-foreground font-medium">User</th>
                      <th className="text-left px-4 py-3 text-muted-foreground font-medium">Role</th>
                      <th className="text-left px-4 py-3 text-muted-foreground font-medium">Status</th>
                      <th className="text-left px-4 py-3 text-muted-foreground font-medium">Joined</th>
                      <th className="text-left px-4 py-3 text-muted-foreground font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className="border-b border-border/20 hover:bg-card/30 transition-colors">
                        <td className="px-4 py-3">
                          <p className="font-medium text-foreground">{user.name}</p>
                          <p className="text-muted-foreground text-xs">{user.email}</p>
                          <p className="text-muted-foreground text-xs">@{user.username}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${
                            user.role === "super_admin"
                              ? "bg-indigo-900 text-indigo-300 border-indigo-700"
                               : "bg-card text-muted-foreground border-border"
                          }`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {user.isActive
                            ? <span className="flex items-center gap-1 text-green-400 text-xs"><CheckCircle className="w-3.5 h-3.5" /> Active</span>
                            : <span className="flex items-center gap-1 text-red-400 text-xs"><XCircle className="w-3.5 h-3.5" /> Suspended</span>
                          }
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          {user.role !== "super_admin" && (
                            <button
                              onClick={() => toggleUserSuspend(user.id)}
                              className={`text-xs px-3 py-1 rounded-lg border transition-colors ${
                                user.isActive
                                  ? "border-red-700 text-red-400 hover:bg-red-900/30"
                                  : "border-green-700 text-green-400 hover:bg-green-900/30"
                              }`}
                            >
                              {user.isActive ? "Suspend" : "Activate"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <p className="text-muted-foreground text-sm">Page {userPage} of {userPages}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                    disabled={userPage === 1}
                    className="p-2 rounded-lg border border-border text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setUserPage((p) => Math.min(userPages, p + 1))}
                    disabled={userPage === userPages}
                    className="p-2 rounded-lg border border-border text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Feature Flags ── */}
          {activeTab === "flags" && (
            <div>
              <h1 className="text-2xl font-bold mb-6">Feature Flags</h1>
              <div className="space-y-3">
                {flags.map((flag) => (
                  <div key={flag.featureKey} className="bg-card rounded-2xl border border-border px-5 py-4 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-foreground">{flag.featureName}</p>
                      <p className="text-muted-foreground text-xs mt-0.5">{flag.featureKey}</p>
                      {flag.description && <p className="text-muted-foreground text-xs mt-1">{flag.description}</p>}
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold border ${STATUS_COLORS[flag.status]}`}>
                        {STATUS_LABELS[flag.status]}
                      </span>
                      <button
                        onClick={() => toggleFlag(flag.featureKey, flag.status)}
                        disabled={flagUpdating === flag.featureKey}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-card hover:bg-muted text-sm font-medium transition-colors disabled:opacity-50"
                      >
                        {flagUpdating === flag.featureKey
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : flag.status === "live" ? <ToggleRight className="w-4 h-4 text-green-400" /> : <ToggleLeft className="w-4 h-4 text-muted-foreground" />
                        }
                        {flag.status === "rd" ? "→ Beta" : flag.status === "beta" ? "→ Live" : "→ R&D"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Audit Log ── */}
          {activeTab === "audit" && (
            <div>
              <h1 className="text-2xl font-bold mb-6">Audit Log</h1>
              <div className="bg-card rounded-2xl border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left px-4 py-3 text-muted-foreground font-medium">Action</th>
                      <th className="text-left px-4 py-3 text-muted-foreground font-medium">Target</th>
                      <th className="text-left px-4 py-3 text-muted-foreground font-medium">Admin</th>
                      <th className="text-left px-4 py-3 text-muted-foreground font-medium">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log) => (
                      <tr key={String(log.id)} className="border-b border-border/20 hover:bg-card/30 transition-colors">
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded-md bg-indigo-900/50 text-indigo-300 text-xs font-mono">
                            {log.action}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {log.targetType} — {log.targetId}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">{log.adminName}</td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {new Date(log.createdAt).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                    {auditLogs.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">No audit logs yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between mt-4">
                <p className="text-muted-foreground text-sm">Page {auditPage}</p>
                <div className="flex gap-2">
                  <button onClick={() => setAuditPage((p) => Math.max(1, p - 1))} disabled={auditPage === 1}
                    className="p-2 rounded-lg border border-border text-muted-foreground hover:text-foreground disabled:opacity-40">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={() => setAuditPage((p) => p + 1)} disabled={auditLogs.length < 50}
                    className="p-2 rounded-lg border border-border text-muted-foreground hover:text-foreground disabled:opacity-40">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
}